import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useLocaleContext } from "src/context/locale-context";
import { ProductSection } from "src/components/features/home/ProductSection/ProductSection";
import { FlightHotelIcon } from "src/components/features/home/ProductSection/icons/FlightHotelIcon";
import {
  BigBenWheelSilhouette,
  CloudGroup,
  CurvedBands,
  FlightPathDecoration,
  TvTowerSilhouette,
} from "./AviationDecoration";
import { AvioHotelCard } from "./AvioHotelCard";
import classes from "./AvioCeljojumiCarousel.module.css";
import { getAvioText } from "./avioCeljojumiText";

const VISIBLE_COUNT = 4; // cards visible at once on desktop — matches the .cardWrap 4-up CSS

// The section itself carries no color anymore — the lavender base + its white top/bottom fade
// both live on .decorLayer now (one coherent layer, see its CSS), so there's only one source of
// color/fade to keep in sync instead of two separate gradient systems. Explicitly transparent
// (not left to the shared ProductSection default) since this section never passes `accentColor`.
const SECTION_STYLE = {
  background: "transparent",
  marginTop: "3rem", // 48px
};

// One decorative layer (position:absolute; inset:0 — see .decorLayer) instead of several
// separately-positioned pieces scattered directly on the section, so it has a single crop/mask
// boundary and can't be mistaken for page content. Back-to-front: solid lavender base (on
// .decorLayer itself), two diagonal wave ribbons, a TV tower far-left, Big Ben + a Ferris wheel
// far-right (both anchored flush to their edges, narrow enough to stay in the section's visible
// side margins instead of mostly hidden behind the cards), two small cloud groups above the
// cards, a plane + dotted route well clear of the "Skatīt visus" button.
const DECOR = (
  <div className={classes.decorLayer} aria-hidden="true">
    <CurvedBands className={classes.bands} />
    <TvTowerSilhouette className={classes.cityLeft} />
    <BigBenWheelSilhouette className={classes.cityRight} />
    <CloudGroup className={classes.cloudA} />
    <CloudGroup className={classes.cloudB} />
    <FlightPathDecoration className={classes.flightPath} />
  </div>
);

// There's no real "flight + hotel in one booking" product yet, so this section reuses the real
// cheapHotels data/card (same source as "Lēti viesnīcu piedāvājumi" further down the page) via
// AvioHotelCard instead of showing fabricated flight+hotel offers.
export function AvioCeljojumiCarousel({ data }) {
  const { getStringRes } = useLocaleContext();
  const navigate = useNavigate();
  const trackRef = useRef(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [activeDot, setActiveDot] = useState(0);

  const text = (key) => getAvioText(getStringRes, key);

  // When fewer real deals exist than fit on one screen, size the cards to fill the row instead
  // of leaving dead space on the right (rather than ever padding the list with fake cards).
  const effectiveVisibleCount = Math.min(VISIBLE_COUNT, data?.length || 1);
  const dotCount = Math.ceil((data?.length ?? 0) / effectiveVisibleCount);

  const updateScrollState = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollPrev(el.scrollLeft > 4);
    setCanScrollNext(el.scrollLeft < maxScroll - 4);
    if (dotCount > 1 && el.clientWidth > 0) {
      const page = Math.round(el.scrollLeft / el.clientWidth);
      setActiveDot(Math.min(dotCount - 1, Math.max(0, page)));
    }
  }, [dotCount]);

  useEffect(() => {
    updateScrollState();
    window.addEventListener("resize", updateScrollState);
    return () => window.removeEventListener("resize", updateScrollState);
  }, [updateScrollState]);

  const scrollByPage = (direction) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth, behavior: "smooth" });
  };

  const scrollToDot = (index) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollTo({ left: index * el.clientWidth, behavior: "smooth" });
  };

  if (!data || data.length === 0) return null;

  return (
    <ProductSection
      sectionStyle={SECTION_STYLE}
      sectionClassName={classes.section}
      decor={DECOR}
      icon={<FlightHotelIcon size={72} />}
      title={text("home.section.avio.title")}
      subtitle={text("home.section.avio.subtitle")}
      viewAllLabel={text("home.section.viewAll")}
      onViewAll={() => navigate("/hotels")}
      dotCount={dotCount}
      activeDot={activeDot}
      onDotClick={scrollToDot}>
      <div className={classes.carouselWrap}>
        {canScrollPrev && (
          <button
            type="button"
            aria-label="previous"
            className={classes.sideArrow}
            onClick={() => scrollByPage(-1)}>
            <IconChevronLeft size={20} stroke={1.75} />
          </button>
        )}
        <div
          className={classes.track}
          ref={trackRef}
          style={{ "--visible-count": effectiveVisibleCount }}
          onScroll={updateScrollState}>
          {data.map((item) => (
            <div key={item.id} className={classes.cardWrap}>
              <AvioHotelCard item={item} />
            </div>
          ))}
        </div>
        {canScrollNext && (
          <button
            type="button"
            aria-label="next"
            className={`${classes.sideArrow} ${classes.sideArrowNext}`}
            onClick={() => scrollByPage(1)}>
            <IconChevronRight size={20} stroke={1.75} />
          </button>
        )}
      </div>
    </ProductSection>
  );
}
